import React from 'react';
import { Checkbox } from '@americanexpress/dls-react';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import Selectors from 'src/utils/selectors';
import LiteralsService from 'src/services/literals/LiteralsService';

interface TransactionsTableHeaderCheckboxProps {
    noOfSelectedTransactions: number;
    noOfFilteredTransactions: number;
    setAllTransactionsSelect: (value: boolean) => void;
}

/**
 * TransactionsTableHeaderCheckbox component - this is a checkbox that sits outside the table and allows the user to
 * select all transactions. Require a checkbox with label "Select All" for ADA compliance.
 * @param noOfSelectedTransactions - this is the number of transactions that are currently selected
 * @param noOfFilteredTransactions - this is the number of transactions that are currently displayed
 * @param setAllTransactionsSelect - this is a function that will set all transactions to be selected or unselected
 */
export default function TransactionsTableHeaderCheckbox({
    noOfSelectedTransactions,
    noOfFilteredTransactions,
    setAllTransactionsSelect
}: TransactionsTableHeaderCheckboxProps): JSX.Element {
    const handleHeaderRowCheckChange = (): void => {
        // If some or all transactions are selected, unselect all transactions
        if (noOfSelectedTransactions > 0) {
            trackClick(location.pathname, adobeTags.Transactions.UncheckAll);
            setAllTransactionsSelect(false);
        }
        // If no transactions are selected, select all transactions
        else if (noOfSelectedTransactions === 0) {
            trackClick(location.pathname, adobeTags.Transactions.CheckAll);
            setAllTransactionsSelect(true);
        }
    };

    return (
        <Checkbox
            name={LiteralsService.getLiteral('TransactionsTableHeaderCheckbox.name')}
            label={LiteralsService.getLiteral('TransactionsTableHeaderCheckbox.selectAll')}
            data-cy={Selectors.Transactions.TRANSACTIONS_TABLE_HEADER_CHECKBOX}
            id="table-header-checkbox"
            checked={noOfSelectedTransactions === noOfFilteredTransactions && noOfFilteredTransactions > 0}
            indeterminate={noOfSelectedTransactions > 0 && noOfSelectedTransactions < noOfFilteredTransactions}
            onClick={handleHeaderRowCheckChange}
        />
    );
}
